import { Component, DestroyRef, inject, OnInit } from "@angular/core";
import { ActivatedRoute, Router, RouterOutlet } from "@angular/router";
import { JobService } from "../../../../domains/jobs/services/job.service";
import { UiToastService } from "../../../../shared/components/toast/ui-toast.service";
import { Location } from "@angular/common";
import { SkeletonModule } from "primeng/skeleton";
import { LotService } from "../../../../domains/lots/services/lot.service";
import { AreaService } from "../../../../domains/areas/services/area.service";
import { JobIssueService } from "../../../../domains/jobIssues/services/job-issue-service";
import { MediaService } from "../../../../domains/media/services/media.service";
import { BqInspectionDataService } from "../../../../domains/jobs/services/bq-inspection-data.service";
import { firstValueFrom } from "rxjs/internal/firstValueFrom";
import { AreaConfigurationService } from "../../../../domains/areas/services/area-configuration.service";
import { ZoneService } from "../../../../domains/zones/services/zone.service";
import { LoggerService } from "../../../../shared/services/logger.service";
import { AssertionIssuesService } from "../../../../domains/assertionIssues/services/assertion-issues.service";
import { AssertionService } from "../../../../domains/assertions/services/assertion.service";
import { ComponentService } from "../../../../domains/components/services/component.service";
import { FeatureFlag, FeatureService } from "../../../../core/feature-flags/feature.service";
import { ImageHydrationService } from "../../../../core/sync/image-sync/image-hydration.service";
import { CustomerCareManagerScheduleService } from "../../../../domains/jobs/services/customer-care-manager-schedule.service";
import { BackgroundRefreshCoordinator } from "../../../../shared/utils/background-refresh-coordinator";
import { MissingDataCheckOptions, MissingDataService } from "../../../../shared/utils/missing-data-service";

@Component({
  selector: "app-bq7-root",
  standalone: true,
  imports: [RouterOutlet, SkeletonModule],
  providers: [BqInspectionDataService],
  templateUrl: "./bq7-root.component.html",
  styleUrl: "./bq7-root.component.scss"
})
export class Bq7RootComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private jobId = 0;

  constructor(
    private areaConfigurationService: AreaConfigurationService,
    private areasService: AreaService,
    private assertionIssuesService: AssertionIssuesService,
    private assertionsService: AssertionService,
    private componentsService: ComponentService,
    private jobIssueService: JobIssueService,
    private jobService: JobService,
    private location: Location,
    private logger: LoggerService,
    private lotService: LotService,
    private mediaService: MediaService,
    private route: ActivatedRoute,
    private toastService: UiToastService,
    private zoneService: ZoneService,
    private router: Router,
    protected bqInspectionDataService: BqInspectionDataService,
    private featureService: FeatureService,
    private imageHydrationService: ImageHydrationService,
    private customerCareManagerScheduleService: CustomerCareManagerScheduleService,
    private missingDataService: MissingDataService
  ) { }

  async ngOnInit(): Promise<void> {
    this.jobId = parseInt(this.route.snapshot.paramMap.get("jobId") ?? "0");

    // Subscribes to the CCM schedule's background-refresh notification before the initial load runs, so
    // a refresh that lands mid-load is never silently missed, and re-reads local data on every later one.
    const coordinator = new BackgroundRefreshCoordinator(
      this.customerCareManagerScheduleService.scheduleRefreshed,
      redirectOnMissingData => this.loadJobData(this.jobId, redirectOnMissingData),
      this.destroyRef
    );
    this.bqInspectionDataService.isRefreshingInBackground = this.customerCareManagerScheduleService.isFetchingScheduleSignal;
    this.bqInspectionDataService.dataUpdatedPulse = coordinator.dataUpdatedPulse;

    await coordinator.runInitialLoad();
  }

  /**
   * Loads (or re-loads) all local data for the given BQ7 job. `redirectOnMissingData` should only be
   * true for the initial load — a background-refresh-triggered reload should never yank the user away
   * from a page they're already interacting with, so it just logs and no-ops instead.
   */
  private async loadJobData(jobId: number, redirectOnMissingData: boolean): Promise<boolean> {
    const bq7Job = await this.jobService.getJobById(jobId);
    const missingDataOptions = this.buildMissingDataOptions(jobId, redirectOnMissingData, bq7Job?.lotId);

    if (this.missingDataService.isMissingData(bq7Job, missingDataOptions.job)) {
      return false;
    }

    const lot = await this.lotService.getLotById(bq7Job.lotId);
    if (this.missingDataService.isMissingData(lot, missingDataOptions.lot)) {
      return false;
    }

    const zones = await this.zoneService.getZonesByLotId(bq7Job.lotId);
    if (this.missingDataService.isMissingData(zones, missingDataOptions.zones)) {
      return false;
    }

    const startTime = performance.now();

    const [areas, jobIssues, assertionIssues, components, assertions] = await Promise.all([
      this.areasService.getAreasByLotId(bq7Job.lotId),
      this.jobIssueService.getJobIssuesForJob(bq7Job.id),
      this.assertionIssuesService.getAssertionIssuesForJob(bq7Job.id),
      this.componentsService.getComponentsForJob(bq7Job.id),
      this.assertionsService.getAssertionsForJob(bq7Job.id),
    ]);

    const areasByBuildQualityStep = areas.filter(area => !!area.buildQualityStepScope === false || area.buildQualityStepScope === bq7Job.buildQualityStep);
    const zonesForAreas = zones.filter(zone => areasByBuildQualityStep.some(area => area.zoneId === zone.id));

    const [issueMedia, assertionMedia] = await Promise.all([
      this.mediaService.getMediaForJobIssues([...jobIssues, ...assertionIssues]),
      this.mediaService.getMediaForAssertions(assertions),
    ]);

    const endTime = performance.now();
    this.logger.info(`[Bq7RootComponent] Time taken to fetch job details from indexedDB: ${endTime - startTime} ms.`);

    firstValueFrom(this.areaConfigurationService.getConfiguration(bq7Job.divisionId))
      .then(areasConfig => {
        this.bqInspectionDataService.areasConfig.set(areasConfig);
      });

    this.bqInspectionDataService.job.set(bq7Job);
    this.bqInspectionDataService.lot.set(lot);
    this.bqInspectionDataService.areas.set(areasByBuildQualityStep);
    this.bqInspectionDataService.jobIssues.set(jobIssues);
    this.bqInspectionDataService.media.set([...issueMedia, ...assertionMedia]);
    this.bqInspectionDataService.zones.set(zonesForAreas);
    this.bqInspectionDataService.assertionIssues.set(assertionIssues);
    this.bqInspectionDataService.components.set(components);
    this.bqInspectionDataService.assertions.set(assertions);
    this.bqInspectionDataService.isCcmInspection.set(true);
    await this.loadOpenBq6IssuesForCarryover(bq7Job.id);
    return true;
  }

  /**
   * Loads the still-open BQ6 (CM self-inspection) assertion issues for this lot so the CCM can review and
   * carry them into their BQ7 audit. Gated behind the BQ6 feature flag; when disabled the carryover
   * signals stay empty, so the badge and carryover stepper never surface.
   */
  private async loadOpenBq6IssuesForCarryover(bq7JobId: number): Promise<void> {
    try {
      const isBq6Enabled = await firstValueFrom(this.featureService.isEnabled(FeatureFlag.EnableBQ6));
      if (!isBq6Enabled) {
        return;
      }

      const assertionIdentifiers = (this.bqInspectionDataService.assertions() ?? []).map(assertion => assertion.identifier);

      const openBq6Issues = await this.assertionIssuesService.getOpenCarryoverIssuesForAssertions(
        assertionIdentifiers,
        bq7JobId
      );
      if (openBq6Issues.length === 0) {
        return;
      }

      const bq6JobIds = [...new Set(openBq6Issues.map(x => x.jobId))];
      await this.imageHydrationService.hydrateByJobIds(bq6JobIds);

      const bq6IssueMedia = await this.mediaService.getMediaForJobIssues(openBq6Issues);
      const bq6IssueVendors = await this.jobIssueService.getLatestVendorAssignmentsForIssues(
        openBq6Issues.map(issue => issue.identifier)
      );

      this.bqInspectionDataService.carryoverIssues.set(openBq6Issues);
      this.bqInspectionDataService.carryoverMedia.set(bq6IssueMedia);
      this.bqInspectionDataService.carryoverVendors.set(bq6IssueVendors);
    } catch (error) {
      this.logger.error("[Bq7RootComponent] Failed to load open BQ6 issues for carryover.", error);
    }
  }

  private buildMissingDataOptions(jobId: number, redirectOnMissingData: boolean, lotId?: number): Record<"job" | "lot" | "zones", MissingDataCheckOptions> {
    return {
      job: {
        redirectOnMissingData,
        redirect: { type: "navigate", route: ["/ccm/schedule"] },
        errorMessage: "Error loading job data for BQ7. Please try again.",
        logMessage: `Background refresh could not find job ${jobId}`,
        componentTag: "Bq7RootComponent"
      },
      lot: {
        redirectOnMissingData,
        redirect: { type: "back" },
        errorMessage: "Error loading related lot data for BQ7. Please try again.",
        logMessage: `Background refresh could not find lot ${lotId}`,
        componentTag: "Bq7RootComponent"
      },
      zones: {
        redirectOnMissingData,
        redirect: { type: "back" },
        errorMessage: "Error loading related zone data for BQ7. Please try again.",
        logMessage: `Background refresh could not find zones for lot ${lotId}`,
        componentTag: "Bq7RootComponent"
      }
    };
  }
}
